/**
 * Pill — status badge.
 *
 * One outlined recipe with a tone per state, replacing the five parallel badge
 * systems the portal had accumulated. `dot` adds the leading indicator dot used
 * by live/connection states; `pulsing` animates it.
 */

import type { HTMLAttributes, ReactNode } from 'react';
import styles from './pill.module.css';

export type PillTone =
  | 'neutral'
  | 'active'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'locked'
  | 'brand'
  | 'article'
  | 'video'
  | 'quiz'
  | 'gameplay';

interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: PillTone;
  /** Filled background instead of outline-only. */
  solid?: boolean;
  /** Recessed chip: neutral surface and border, tone only on the label. */
  sunken?: boolean;
  /** Leading indicator dot. */
  dot?: boolean;
  /** Animate the dot — live states only. */
  pulsing?: boolean;
  className?: string;
  children?: ReactNode;
}

export function Pill({
  tone = 'neutral',
  solid = false,
  sunken = false,
  dot = false,
  pulsing = false,
  className,
  children,
  ...rest
}: PillProps) {
  const composed = [
    styles.base,
    styles[tone],
    solid ? styles.solid : null,
    sunken ? styles.sunken : null,
    dot ? styles.dot : null,
    dot && pulsing ? styles.pulsing : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={composed} {...rest}>
      {children}
    </span>
  );
}
